import * as React from 'react';

/**
 * Whether a media query matches, read live.
 *
 * For decisions CSS cannot make — what a component *does* at a size, not how
 * it looks. How it looks belongs in a responsive utility.
 */
export function useMediaQuery(query: string) {
  const subscribe = React.useCallback(
    (onChange: () => void) => {
      const mql = window.matchMedia(query);
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    },
    [query],
  );
  return React.useSyncExternalStore(
    subscribe,
    () => window.matchMedia(query).matches,
    () => false,
  );
}

/**
 * Whether the viewport is at least a named breakpoint wide.
 *
 * The width is read from the `--breakpoint-*` token rather than written here,
 * so a component's behaviour switches at exactly the width its `lg:` classes do.
 */
export function useBreakpoint(name: 'sm' | 'lg') {
  const width = React.useMemo(
    () =>
      getComputedStyle(document.documentElement).getPropertyValue(`--breakpoint-${name}`).trim(),
    [name],
  );
  return useMediaQuery(`(min-width: ${width})`);
}
